import AppTable from '@/components/AppTable';
import { CloseOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Card } from 'antd';
import React, { useMemo } from 'react';
import { formatPeriod, columnSorter } from '@/utils/formatters';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';

const PieceTrackingTable = React.forwardRef(
  (
    {
      prizeData,
      selectedPrizeOption,
      selectedPrizeRecord,
      setOpenDetail,
      setRecordDetail,
      setOpenTracking,
      highlight,
      dataSource,
      loading,
      refetch,
      pageIndex,
      pageSize,
      setPageIndex,
      setPageSize,
    },
    ref
  ) => {
    const isBreakpoint = useMediaQuery(`(max-width: ${mediaQueryPoints.mdPlus}px)`);

    const prizeTrackingDisplay = useMemo(() => {
      return prizeData?.find((item) => item.id === selectedPrizeRecord?.id);
    }, [selectedPrizeRecord?.id, prizeData]);

    const filteredData = useMemo(() => {
      if (!dataSource || dataSource.length === 0) return [];

      return dataSource.filter((item) => item.missing_pieces >= 0);
    }, [dataSource]);

    const prizeTrackingCols = [
      {
        title: 'No.',
        dataIndex: 'id',
        key: 'id',
        render: (_, __, index) => {
          return <span className="">{index + 1}</span>;
        },
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: () => <span className="">{prizeTrackingDisplay?.name}</span>,
      },
      {
        title: 'Period',
        dataIndex: 'period',
        key: 'period',
        render: () => {
          const periodStart = prizeTrackingDisplay?.start_date;
          const periodEnd = prizeTrackingDisplay?.end_date;
          return <span>{formatPeriod(periodStart, periodEnd)}</span>;
        },
      },
      {
        title: 'Pieces Collected',
        dataIndex: 'pieces_collected',
        key: 'pieces_collected',
        render: (pieces, record) => (
          <span className="">{`${pieces}/${record?.total_pieces}` || 0}</span>
        ),
      },
      {
        title: 'Number of Users',
        dataIndex: 'number_of_user',
        key: 'number_of_user',
        render: (userId, record, index) => {
          return <span>{record?.number_of_users || '0'}</span>;
        },
      },
      {
        title: 'Actions',
        key: 'Actions',
        fixed: 'right',
        render: (_, record) => (
          <Button
            type="link"
            onClick={() => {
              setOpenDetail(true);
              setRecordDetail(record);
            }}
            icon={isBreakpoint ? <EyeOutlined /> : null}
          >
            {isBreakpoint ? null : 'View Details'}
          </Button>
        ),
      },
    ];

    return (
      <Card
        ref={ref}
        title={<h3>Prize Piece Collection Tracking</h3>}
        extra={
          <Button
            type="text"
            onClick={() => {
              setOpenTracking(false);
              setOpenDetail(false);
            }}
            className="px-2"
          >
            <CloseOutlined />
          </Button>
        }
        className={`${highlight ? 'animate-highlight' : 'card-highlight'} w-full mt-4`}
      >
        <AppTable
          columns={prizeTrackingCols}
          dataSource={filteredData}
          loading={loading}
          hasRowSelect={false}
          hasPagination={false}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPageChange={(page, size) => {
            setPageIndex(page);
          }}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
          }}
          onRefresh={refetch}
          bordered={false}
        />
      </Card>
    );
  }
);

PieceTrackingTable.displayName = 'PieceTrackingTable';

export default PieceTrackingTable;
